<template>
    <SearchCardLayout
        v-if="props.data"
        class="project-card"
        :class="{ archived: props.data.status == 0 }"
        :disabled="!userPermissionConfig.canOpenProject">
        <template #left>
            <ArboThumbnail
                class="arbo-ui-styles"
                style="cursor: default"
                :thumbnail="props.data.thumbnail || ''"
                :size="'32px'"
                :disabled="false"
                :disableHover="true" />
        </template>
        <template #quick-action>
            <ArboButton
                v-if="userPermissionConfig?.canOpenProject"
                class="arbo-ui-styles"
                :label="'Directory'"
                :prefix-icon="'Sitemap'"
                size="small"
                @click.stop="goToDirectory" />
        </template>
        <template #detail="{ width }">
            <div class="project-detail" :style="{ width: `${width}px` }">
                <div class="breadcrumbs" :class="{ 'has-archived': props.data.status == 0 }">
                    <ArboIcon v-if="props.data.status == 0" name="BoxArchive" />
                    <TessBreadcrumb class="arbo-ui-styles" v-bind="{ ...breadcrumbTemp, width, disabled: true }" />
                </div>
                <ProgressBar v-bind="projectBarProps">
                    <div class="row">
                        <div class="woohoo-progress">
                            <span class="woohoo-icon">
                                <arbo-icon name="ChartPie" />
                            </span>
                            <span> {{ formatPercentage(props.data.woohoo_progress) }}</span>
                        </div>
                    </div>
                </ProgressBar>
            </div>
        </template>

        <template #more-actions="{ show, hide }">
            <TessMoreActions class="arbo-ui-styles" v-bind="projectMoreAction" size="small" @show="show" @hide="hide" />
        </template>
        <template #progress>
            <ProgressBar class="arbo-ui-styles progress-bar" v-bind="projectBarProps" />
        </template>
    </SearchCardLayout>
</template>
<script setup lang="ts">
import SearchCardLayout from './CardLayout.vue';
import ProgressBar from '../elements/ProgressBar.vue';
import { formatPercentage, getWS_Code } from 'src/utils';
import type { ProjectCardData } from 'src/types/entity.types';
import { computed } from 'vue';

import { baseLinkConfig, clickActionHandler } from 'src/plugins';
import { storeToRefs } from 'pinia';
import { useUserStore } from 'src/stores/userStore';
import { createCommonLink } from '@arbocollab/arbo-ui-components';
type Props = {
    data?: ProjectCardData;
};
const props = defineProps<Props>();
const { userPermissionConfig } = storeToRefs(useUserStore());

const wscode = computed(() => getWS_Code() || '');

const baseDomain = sessionStorage.getItem('BASE_URL') || '';

const projectDirLink = computed(() => {
    const { parent_id, id } = props.data || {};
    const ws_code = wscode.value;
    if (!id) return '';
    return parent_id
        ? `${baseDomain}/${ws_code}/pl/dir/${parent_id}?f=${id}`
        : `${baseDomain}/${ws_code}/pl/dir?f=${id}`;
});

const goToDirectory = (e: MouseEvent) => {
    clickActionHandler(e, projectDirLink.value);
};

const copyLink = () => {
    navigator.clipboard.writeText(projectDirLink.value).catch(console.error);
};

const projectBarProps = computed(() => ({
    forceComplete: props.data?.is_completed == 1 || props.data?.woohoo_progress == 100,
    actual: Number(props.data?.woohoo_progress),
    expected: Number(props.data?.expected_woohoo_progress),
    lastUpdate: props.data?.updated_at || '',
    unit: props.data?.woohoo_metric || ''
}));

const openMode = (e: MouseEvent, mode: 'eg' | 'as') => {
    const { parent_id, id } = props.data || {};
    const ws_code = wscode.value;
    if (!id) return;
    const link = parent_id
        ? `${baseDomain}/${ws_code}/pl/dir/${parent_id}?f=${id}&o=${mode}`
        : `${baseDomain}/${ws_code}/pl/dir?f=${id}&o=${mode}`;
    clickActionHandler(e, link);
};

const openSettings = (e: MouseEvent) => openMode(e, 'eg');
const openAccessSettings = (e: MouseEvent) => openMode(e, 'as');

const projectPermission = computed(() => ({
    entitySetting: props.data?.permission?.has_edit_permission === 1,
    accessSetting: props.data?.permission?.has_edit_permission === 1
}));

const linkConfig = computed(() => {
    const id = props.data?.id || '';
    const config = {
        ...baseLinkConfig(),
        domain: id,
        entity_id: id,
        take_id: id,
        woohoo_id: id
    };
    const response = {
        project: createCommonLink({ type: 'project', config }),
        take_prk: createCommonLink({ type: 'project', config }),
        take_review: createCommonLink({ type: 'review_take', config }),
        take_update: createCommonLink({ type: 'update', config }),
        take_woohoo: createCommonLink({ type: 'woohoo', config })
    };
    return response;
});

const isTakeLevel = computed(() => props.data?.entity_type?.code === 'take');

const openTakeIn = (_e: MouseEvent, type: 'take_prk' | 'take_review' | 'take_update' | 'take_woohoo') => {
    const link = linkConfig.value[type].url;
    clickActionHandler(_e, link);
};
const projectMoreAction = computed(() => {
    const perms = userPermissionConfig.value;
    return {
        menuItems: [
            {
                items: [
                    isTakeLevel.value
                        ? {
                              id: 'openIn',
                              label: 'Open in',
                              icon: 'ArrowUpRightFromSquare',
                              visible:
                                  perms.canOpenTakeUpdate ||
                                  perms.canOpenReview ||
                                  perms.canOpenTakeWoohoo ||
                                  perms.canOpenProject,
                              type: 'submenu',
                              subMenu: [
                                  {
                                      id: 'pj-dir',
                                      label: 'Project Directory',
                                      icon: 'Sitemap',
                                      action: (e: MouseEvent) => openTakeIn(e, 'take_prk'),
                                      visible: perms.canOpenProject,
                                      type: 'action'
                                  },
                                  {
                                      id: 'take_review',
                                      label: 'Review',
                                      icon: 'PenRuler',
                                      action: () => {
                                          window.open(linkConfig.value.take_review.url, '_blank');
                                      },
                                      visible: perms.canOpenReview,
                                      type: 'action'
                                  },
                                  {
                                      id: 'update',
                                      label: 'Update',
                                      icon: 'UserCheck',
                                      action: (e: MouseEvent) => openTakeIn(e, 'take_update'),
                                      visible: perms.canOpenTakeUpdate,
                                      type: 'action'
                                  },
                                  {
                                      id: 'pj-dir',
                                      label: 'Woohoo Details',
                                      icon: 'ChartPie',
                                      action: (e) => openTakeIn(e, 'take_woohoo'),
                                      visible: perms.canOpenTakeWoohoo,
                                      type: 'action'
                                  }
                              ]
                          }
                        : {
                              id: 'openIn',
                              label: 'Open  in',
                              icon: 'ArrowUpRightFromSquare',
                              visible: perms.canOpenProject,
                              type: 'submenu',
                              subMenu: [
                                  {
                                      id: 'projectDirectory',
                                      label: 'Project Directory',
                                      icon: 'Sitemap',
                                      action: goToDirectory,
                                      visible: perms.canOpenProject,
                                      tooltip: '',
                                      type: 'action'
                                  }
                              ]
                          }
                ]
            }
        ],
        additionalOptions: projectPermission.value.accessSetting
            ? {
                  label: 'Access Settings',
                  icon: 'UserLock',
                  id: 'accessSettings',
                  visible: projectPermission.value.accessSetting,
                  tooltip: 'Access Settings',
                  type: 'action',
                  action: openAccessSettings
              }
            : undefined,
        actionsTop: [
            {
                id: '1',
                label: '',
                icon: 'ScrewdriverWrench',
                action: openSettings,
                visible: projectPermission.value.entitySetting,
                preventClose: false,
                tooltip: 'Settings',
                type: 'action'
            },
            {
                id: '2',
                label: '',
                icon: 'Link',
                action: copyLink,
                visible: true,
                tooltip: 'Copy link',
                type: 'action',
                preventClose: false
            }
        ]
    };
});

const breadcrumbTemp = computed(() => ({
    url: createCommonLink({
        type: 'project',
        config: {
            ...baseLinkConfig(),
            entity_id: props.data?.id || ''
        }
    }).url,
    items: [
        ...(props.data?.entity_path?.map((item) => ({
            name: item.name,
            id: item.id,
            url: createCommonLink({
                type: 'project',
                config: {
                    ...baseLinkConfig(),
                    entity_id: item.id
                }
            }).url
        })) || []),
        {
            name: props.data?.name,
            id: props.data?.id,
            url: createCommonLink({
                type: 'project',
                config: {
                    ...baseLinkConfig(),
                    entity_id: props.data?.id || ''
                }
            }).url
        }
    ],
    disabled: false,
    replaceCurrent: false,
    separator: '>',
    lastItemWidth: 150,
    canCopy: true,
    width: 350
}));
</script>
<style lang="scss" scoped>
.woohoo-progress {
    height: 16px;
    margin-right: 4px;
    display: flex;
    align-items: center;
    gap: 8px;

    .woohoo-icon {
        width: 16px;
        height: 16px;
        background-color: #0000000d;

        margin: 0 4px;
        border-radius: 2px;
    }
}
</style>
